import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import axios from 'axios';
import { 
  loginWithEmail, 
  registerWithEmail, 
  loginWithGoogle, 
  logout as firebaseLogout,
  onAuthChange,
  getIdToken,
  resendVerificationEmail,
  reloadUser,
} from '../lib/firebase';
import { authApi, GoogleRegistrationRequiredData } from '../lib/api';

/**
 * Extract user-friendly error message from various error types
 * Handles axios errors, Firebase errors, and standard errors
 */
function extractErrorMessage(error: unknown, fallback: string): string {
  // Handle axios errors - extract message from response body
  if (axios.isAxiosError(error)) {
    const responseData = error.response?.data;
    if (responseData?.error) {
      return responseData.error;
    }
    if (responseData?.message) {
      return responseData.message;
    }
    // Fallback for specific status codes
    if (error.response?.status === 409) {
      return 'This account already exists. Please sign in instead.';
    }
    if (error.response?.status === 401) {
      return 'Invalid credentials. Please try again.';
    }
    if (error.response?.status === 404) {
      return 'Account not found. Please register first.';
    }
  }
  
  // Handle standard errors
  if (error instanceof Error) {
    return error.message;
  }
  
  return fallback;
}

export interface User {
  id: string;
  firebaseUid: string;
  email: string;
  firstName: string;
  lastName: string;
  role: 'STUDENT' | 'PROFESSOR' | 'ADMIN';
  avatarUrl?: string;
  bio?: string;
  stripeCustomerId?: string;
  stripeAccountId?: string;
  createdAt: string;
  emailVerified: boolean;
  // Professor identity verification status (Didit KYC)
  verificationStatus?: 'UNVERIFIED' | 'PENDING' | 'VERIFIED' | 'FAILED' | 'EXPIRED';
  verifiedAt?: string;
}

// Data for pending Google registration (when user needs to select role)
export interface PendingGoogleRegistration {
  firebaseUid: string;
  email: string;
  firstName: string;
  lastName: string;
  avatarUrl: string | null;
  provider: string;
  // For existing users who might want to switch roles
  existingUser?: {
    id: string;
    currentRole: 'STUDENT' | 'PROFESSOR' | 'ADMIN';
  };
}

interface AuthState {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  pendingVerification: boolean; // User registered but email not verified
  pendingGoogleRegistration: PendingGoogleRegistration | null; // New Google user needs role selection
  
  // Actions
  initAuth: () => void;
  login: (email: string, password: string) => Promise<void>;
  loginWithGoogle: () => Promise<void>;
  completeGoogleRegistration: (role: 'STUDENT' | 'PROFESSOR') => Promise<void>;
  switchUserRole: (newRole: 'STUDENT' | 'PROFESSOR') => Promise<void>;
  clearPendingGoogleRegistration: () => void;
  register: (data: {
    email: string;
    password: string;
    firstName: string;
    lastName: string;
    role: 'STUDENT' | 'PROFESSOR';
  }) => Promise<void>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
  setUser: (user: User | null) => void;
  clearError: () => void;
  resendVerification: () => Promise<void>;
  checkEmailVerification: () => Promise<boolean>;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      isAuthenticated: false,
      isLoading: true,
      error: null,
      pendingVerification: false,
      pendingGoogleRegistration: null,

      initAuth: () => {
        onAuthChange(async (firebaseUser) => {
          if (firebaseUser) {
            // Check if email is verified
            if (!firebaseUser.emailVerified) {
              // User exists but email not verified - show verification page
              set({ 
                user: null, 
                isAuthenticated: false, 
                pendingVerification: true,
                isLoading: false 
              });
              return;
            }
            
            try {
              const response = await authApi.me();
              if (response.data.success && response.data.data) {
                const userData = response.data.data as User;
                set({ 
                  user: { ...userData, emailVerified: true }, 
                  isAuthenticated: true,
                  pendingVerification: false,
                  isLoading: false 
                });
              } else {
                set({ user: null, isAuthenticated: false, pendingVerification: false, isLoading: false });
              }
            } catch {
              set({ user: null, isAuthenticated: false, pendingVerification: false, isLoading: false });
            }
          } else {
            set({ user: null, isAuthenticated: false, pendingVerification: false, isLoading: false });
          }
        });
      },

      login: async (email: string, password: string) => {
        set({ isLoading: true, error: null });
        try {
          const firebaseUser = await loginWithEmail(email, password);
          
          // SECURITY: Check email verification BEFORE allowing login
          if (!firebaseUser.emailVerified) {
            set({ 
              isLoading: false, 
              pendingVerification: true,
              isAuthenticated: false,
            });
            throw new Error('EMAIL_NOT_VERIFIED');
          }
          
          const idToken = await getIdToken();
          
          if (!idToken) {
            throw new Error('Failed to get authentication token');
          }

          const response = await authApi.login(idToken);
          
          if (response.data.success && response.data.data) {
            const userData = (response.data.data as { user: User }).user;
            set({ 
              user: { ...userData, emailVerified: true }, 
              isAuthenticated: true,
              pendingVerification: false,
              isLoading: false 
            });
          } else {
            throw new Error(response.data.error || 'Login failed');
          }
        } catch (error) {
          const message = error instanceof Error ? error.message : 'Login failed';
          if (message !== 'EMAIL_NOT_VERIFIED') {
            set({ error: message, isLoading: false });
          }
          throw error;
        }
      },

      loginWithGoogle: async () => {
        set({ isLoading: true, error: null, pendingGoogleRegistration: null });
        try {
          const firebaseUser = await loginWithGoogle();
          
          // Google accounts are pre-verified, but check just in case
          if (!firebaseUser.emailVerified) {
            set({ 
              isLoading: false, 
              pendingVerification: true,
              isAuthenticated: false,
            });
            throw new Error('EMAIL_NOT_VERIFIED');
          }
          
          const idToken = await getIdToken();
          
          if (!idToken) {
            throw new Error('Failed to get authentication token');
          }

          const response = await authApi.login(idToken);
          
          // Check if this is a new Google user who needs to select a role
          if (response.data.code === 'GOOGLE_REGISTRATION_REQUIRED') {
            const googleData = response.data.data as GoogleRegistrationRequiredData;
            set({ 
              isLoading: false,
              pendingGoogleRegistration: {
                firebaseUid: googleData.firebaseUid,
                email: googleData.email,
                firstName: googleData.firstName,
                lastName: googleData.lastName,
                avatarUrl: googleData.avatarUrl,
                provider: googleData.provider,
              },
            });
            // Throw a specific error so the UI knows to show the role modal
            throw new Error('GOOGLE_REGISTRATION_REQUIRED');
          }
          
          if (response.data.success && response.data.data) {
            const userData = (response.data.data as { user: User }).user;
            set({ 
              user: { ...userData, emailVerified: true }, 
              isAuthenticated: true,
              pendingVerification: false,
              pendingGoogleRegistration: null,
              isLoading: false 
            });
          } else {
            // User doesn't exist, need to register
            set({ isLoading: false });
            throw new Error('REGISTRATION_REQUIRED');
          }
        } catch (error) {
          const message = error instanceof Error ? error.message : 'Google login failed';
          if (message !== 'EMAIL_NOT_VERIFIED' && message !== 'GOOGLE_REGISTRATION_REQUIRED') {
            set({ error: message, isLoading: false });
          }
          throw error;
        }
      },

      completeGoogleRegistration: async (role: 'STUDENT' | 'PROFESSOR') => {
        const { pendingGoogleRegistration } = get();
        
        if (!pendingGoogleRegistration) {
          throw new Error('No pending Google registration');
        }
        
        // If this is an existing user wanting to switch roles
        if (pendingGoogleRegistration.existingUser) {
          // If they selected the same role, just log them in
          if (pendingGoogleRegistration.existingUser.currentRole === role) {
            // Just proceed with login - the user data should already be available
            const idToken = await getIdToken();
            if (idToken) {
              const response = await authApi.login(idToken);
              if (response.data.success && response.data.data) {
                const userData = (response.data.data as { user: User }).user;
                set({ 
                  user: { ...userData, emailVerified: true }, 
                  isAuthenticated: true,
                  pendingVerification: false,
                  pendingGoogleRegistration: null,
                  isLoading: false 
                });
                return;
              }
            }
          }
          
          // User wants to switch roles - call the switch role API
          await get().switchUserRole(role);
          return;
        }
        
        set({ isLoading: true, error: null });
        
        try {
          const idToken = await getIdToken();
          
          if (!idToken) {
            throw new Error('Failed to get authentication token');
          }
          
          const response = await authApi.completeGoogleRegistration({
            idToken,
            role,
            firstName: pendingGoogleRegistration.firstName,
            lastName: pendingGoogleRegistration.lastName,
          });
          
          // Check if this is an existing user with a different role - need to switch
          if (response.data.code === 'ROLE_SWITCH_AVAILABLE') {
            // User wants to switch roles, call the switch role API
            await get().switchUserRole(role);
            return;
          }
          
          if (response.data.success && response.data.data) {
            const userData = (response.data.data as { user: User }).user;
            set({ 
              user: { ...userData, emailVerified: true }, 
              isAuthenticated: true,
              pendingVerification: false,
              pendingGoogleRegistration: null,
              isLoading: false 
            });
          } else {
            throw new Error(response.data.error || 'Google registration failed');
          }
        } catch (error) {
          const message = extractErrorMessage(error, 'Google registration failed');
          set({ error: message, isLoading: false });
          throw new Error(message);
        }
      },

      switchUserRole: async (newRole: 'STUDENT' | 'PROFESSOR') => {
        const { pendingGoogleRegistration } = get();
        
        set({ isLoading: true, error: null });
        
        try {
          const idToken = await getIdToken();
          
          if (!idToken) {
            throw new Error('Failed to get authentication token');
          }
          
          const response = await authApi.switchRole({ idToken, newRole });
          
          if (response.data.success && response.data.data) {
            const userData = (response.data.data as { user: User }).user;
            set({ 
              user: { ...userData, emailVerified: true }, 
              isAuthenticated: true,
              pendingVerification: false,
              pendingGoogleRegistration: null,
              isLoading: false 
            });
          } else {
            throw new Error(response.data.error || 'Failed to switch role');
          }
        } catch (error) {
          const message = extractErrorMessage(error, 'Failed to switch role');
          set({ error: message, isLoading: false, pendingGoogleRegistration: pendingGoogleRegistration });
          throw new Error(message);
        }
      },

      clearPendingGoogleRegistration: () => {
        set({ pendingGoogleRegistration: null });
      },

      register: async ({ email, password, firstName, lastName, role }) => {
        set({ isLoading: true, error: null });
        try {
          // Register with Firebase (this sends verification email)
          await registerWithEmail(email, password);
          const idToken = await getIdToken();
          
          if (!idToken) {
            throw new Error('Failed to get authentication token');
          }

          // Create user in our database
          const response = await authApi.register({
            email,
            firstName,
            lastName,
            role,
            idToken,
          });
          
          if (response.data.success && response.data.data) {
            // SECURITY: Don't set isAuthenticated - user must verify email first
            set({ 
              user: null, // Don't expose user data until verified
              isAuthenticated: false,
              pendingVerification: true,
              isLoading: false 
            });
          } else {
            throw new Error(response.data.error || 'Registration failed');
          }
        } catch (error) {
          const message = error instanceof Error ? error.message : 'Registration failed';
          set({ error: message, isLoading: false });
          throw error;
        }
      },

      logout: async () => {
        try {
          await authApi.logout();
          await firebaseLogout();
          set({ user: null, isAuthenticated: false, pendingVerification: false });
        } catch (error) {
          console.error('Logout error:', error);
          // Still clear local state even if API call fails
          set({ user: null, isAuthenticated: false, pendingVerification: false });
        }
      },

      refreshUser: async () => {
        try {
          const response = await authApi.me();
          if (response.data.success && response.data.data) {
            set({ user: response.data.data as User });
          }
        } catch (error) {
          console.error('Failed to refresh user:', error);
        }
      },

      setUser: (user) => {
        set({ user, isAuthenticated: !!user });
      },

      clearError: () => {
        set({ error: null });
      },

      resendVerification: async () => {
        try {
          await resendVerificationEmail();
        } catch (error) {
          const message = error instanceof Error ? error.message : 'Failed to resend verification email';
          throw new Error(message);
        }
      },

      checkEmailVerification: async () => {
        try {
          const verified = await reloadUser();
          if (verified) {
            // Email is now verified, user can proceed to login
            set({ pendingVerification: false });
            return true;
          }
          return false;
        } catch {
          return false;
        }
      },
    }),
    {
      name: 'ulearning-auth',
      partialize: (state) => ({ 
        user: state.user, 
        isAuthenticated: state.isAuthenticated,
        // Don't persist pendingGoogleRegistration for security - user must complete in same session
      }),
      onRehydrateStorage: () => (state) => {
        // When hydrating from localStorage, if we have a user, temporarily trust it
        // Firebase onAuthStateChanged will confirm/deny the auth state
        if (state?.user) {
          state.isAuthenticated = true;
        }
        // Clear any stale Google registration state on rehydration
        if (state) {
          state.pendingGoogleRegistration = null;
        }
      },
    }
  )
);

